import { ILogin } from "interfaces/ILogin"
import { enRules } from "interfaces/IUser"
import cookies from "js-cookie"
import { jwtDecode } from "jwt-decode"
import { FC, useContext, createContext, useEffect, ReactNode, useState } from "react"
import { toast } from "react-toastify"
import { api } from "services/api"
import { authenticationService } from "services/authenticationService"

interface IUser {
  isAuthenticated: boolean
  rules: enRules[]
  username: string
}

interface IUseAuth {
  signIn: (props: ILogin) => Promise<void>
  signOut: () => void
  user: IUser
}

const initialUserState = {
  isAuthenticated: false,
  rules: [],
  username: "",
}

const AuthContext = createContext<IUseAuth>({} as IUseAuth)

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState({
    ...initialUserState,
    isAuthenticated: true,
  })

  const getRules = async () => {
    try {
      const response = await authenticationService.getUserInfo()
      const resPermissions = response?.data?.user_groups_names ?? []

      setUser({
        isAuthenticated: true,
        username: response.user_login,
        rules: response.user_groups_names,
      })
      cookies.set("rules", resPermissions)
    } catch {
      toast("Erro ao procurar permissões", { type: "error" })
    }
  }

  const signIn = async (data: ILogin): Promise<void> => {
    try {
      const { access } = await authenticationService.postLogin(data)

      const date = new Date()
      date.setHours(date.getHours() + 11)
      cookies.set("authToken", access, {
        expires: date,
      })

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: Unreachable code error
      // eslint-disable-next-line dot-notation
      api.instance.defaults.headers["Authorization"] = `Bearer ${access}`

      await getRules()
    } catch (error: any) {
      if (error?.detail === "No active account found with the given credentials") {
        toast("Credenciais inválidas", { type: "error" })
      }

      toast(error?.message ?? "Ocorreu um erro, contate o administrador", { type: "error" })
    }
  }

  const signOut = () => {
    cookies.remove("authToken")
    setUser(initialUserState)
    api.instance.defaults.headers.Authorization = ""
  }

  const verifyAuth = () => {
    const token = cookies.get("authToken")
    const rules = cookies.get("rules")
    if (token) {
      try {
        const decodedToken = jwtDecode(token) as any
        const currentDate = new Date()

        if (decodedToken.exp * 1000 < currentDate.getTime()) {
          signOut()
          toast("Login expirado", { type: "info" })
          return
        }
        setUser({
          isAuthenticated: true,
          username: decodedToken.username,
          rules: rules ? JSON.parse(rules) : [],
        })
        getRules()
      } catch (err) {
        signOut()
        toast("Login expirado", { type: "info" })
      }
    } else {
      signOut()
    }
  }

  useEffect(() => {
    api.setFuncions({
      signOut,
    })

    verifyAuth()
  }, [])

  return <AuthContext.Provider value={{ signIn, signOut, user }}>{children}</AuthContext.Provider>
}

const useAuth = () => useContext(AuthContext)

export { AuthProvider, useAuth }
